import {
  BookStudyUnitName,
  CreatingResilientAusClassroomsId,
  CreatingResilientClassroomsId,
  GRADE_K_12_VALUE,
  auGettingStartedGradeLevels,
  newGettingStartedGradeLevels,
} from "@constants";
import {
  MIXPANEL_LESSON_TYPE,
  MIXPANEL_PROGRAM_TYPE,
  MIXPANEL_NONE,
} from "@src/constants/analyticsTracking";
import {
  expandAustraliaAgeGroups,
  expandGradeLevel,
  parseAgeGroupValues,
} from "./helpers";

const MIXPANEL_PROGRAM_TYPE_ALIASES: Record<string, string> = {
  "Student Introduction": MIXPANEL_PROGRAM_TYPE.STUDENT_INTRODUCTION,
  [MIXPANEL_PROGRAM_TYPE.STUDENT_INTRODUCTION]:
    MIXPANEL_PROGRAM_TYPE.STUDENT_INTRODUCTION,
  "Educator Wellbeing": MIXPANEL_PROGRAM_TYPE.EDUCATOR_WELL_BEING_SERIES,
  [MIXPANEL_PROGRAM_TYPE.EDUCATOR_WELL_BEING_SERIES]:
    MIXPANEL_PROGRAM_TYPE.EDUCATOR_WELL_BEING_SERIES,
  [MIXPANEL_PROGRAM_TYPE.STUDENT_VOICE_SERIES]:
    MIXPANEL_PROGRAM_TYPE.STUDENT_VOICE_SERIES,
  [MIXPANEL_PROGRAM_TYPE.LESSON_LIBRARY]: MIXPANEL_PROGRAM_TYPE.LESSON_LIBRARY,
  [MIXPANEL_PROGRAM_TYPE.PROFESSIONAL_DEVELOPMENT]:
    MIXPANEL_PROGRAM_TYPE.PROFESSIONAL_DEVELOPMENT,
  [MIXPANEL_PROGRAM_TYPE.TRANSITIONS]: MIXPANEL_PROGRAM_TYPE.TRANSITIONS,
  [MIXPANEL_PROGRAM_TYPE.BRAIN_BREAKS]: MIXPANEL_PROGRAM_TYPE.BRAIN_BREAKS,
  [MIXPANEL_PROGRAM_TYPE.SKILL_BUILDERS]: MIXPANEL_PROGRAM_TYPE.SKILL_BUILDERS,
  [MIXPANEL_PROGRAM_TYPE.CREATING_RESILIENT_CLASSROOMS]:
    MIXPANEL_PROGRAM_TYPE.CREATING_RESILIENT_CLASSROOMS,
  [MIXPANEL_PROGRAM_TYPE.CAREGIVER]: MIXPANEL_PROGRAM_TYPE.CAREGIVER,
};

const KNOWN_MIXPANEL_PROGRAM_TYPES = new Set<string>([
  ...Object.values(MIXPANEL_PROGRAM_TYPE),
  ...Object.keys(MIXPANEL_PROGRAM_TYPE_ALIASES),
  ...Object.values(MIXPANEL_PROGRAM_TYPE_ALIASES),
]);

const AGE_OR_YEAR_GROUP_LABELS = new Set<string>([
  ...auGettingStartedGradeLevels,
  ...newGettingStartedGradeLevels,
  GRADE_K_12_VALUE,
  "Kindy/Reception - Year 2",
  "Year 3-4",
  "Year 5-6",
  "Year 7-8",
  "Year 9-12",
  "Year 4-12",
  "Pre-K & K",
  "Grade 1 & 2",
  "Grade 3 & 4",
  "Grade 5 & 6",
  "Grade 7 & 8",
  "Grade 9 - 12",
  "Grade K-3",
  "Grade 4-6",
  "Grade 7-9",
  "Grade 10-12",
]);

export const normalizeMixpanelProgramType = (value?: string | null): string => {
  if (!value) return "";
  return MIXPANEL_PROGRAM_TYPE_ALIASES[value] || value;
};

export const isKnownMixpanelProgramType = (value?: string | null): boolean => {
  if (!value) return false;
  return KNOWN_MIXPANEL_PROGRAM_TYPES.has(value);
};

export const isAgeOrYearGroupLabel = (value?: string | null): boolean => {
  if (!value) return false;
  return AGE_OR_YEAR_GROUP_LABELS.has(value.trim());
};

const isCaregiverLesson = ({
  lessonType,
  isCaregiver,
}: {
  lessonType?: string | null;
  isCaregiver?: boolean;
}): boolean => {
  if (isCaregiver) return true;
  if (!lessonType) return false;
  return (
    lessonType === MIXPANEL_LESSON_TYPE.CAREGIVER ||
    lessonType === "Caregiver" ||
    lessonType === "7"
  );
};

type ResolveMixpanelProgramNameArgs = {
  lessonType?: string | null;
  isCaregiver?: boolean;
  /** multiCollections[0].programType or explicit program type */
  programType?: string | null;
  /** Already-known program name override */
  programName?: string | null;
  /** multiCollections[0].name — the program the lesson was opened from */
  multiCollectionName?: string | null;
  unitName?: string | null;
  unitId?: string | null;
  unitUniqueName?: string | null;
  courseName?: string | null;
};

const firstNonEmptyProgramLabel = (
  candidates: Array<string | null | undefined>,
  { allowAgeGroups }: { allowAgeGroups: boolean },
): string => {
  for (const candidate of candidates) {
    const value = (candidate ?? "").trim();
    if (!value) continue;
    if (!allowAgeGroups && isAgeOrYearGroupLabel(value)) continue;
    return normalizeMixpanelProgramType(value) || value;
  }
  return "";
};

/**
 * Resolves Mixpanel `program_name` from the program context the lesson was
 * accessed from. Never returns an empty string (avoids Mixpanel "(not set)" /
 * "(empty string)"). Caregiver lessons always map to "Caregiver".
 */
export const resolveMixpanelProgramName = ({
  lessonType,
  isCaregiver,
  programType,
  programName,
  multiCollectionName,
  unitName,
  unitId,
  unitUniqueName,
  courseName,
}: ResolveMixpanelProgramNameArgs): string => {
  if (isCaregiverLesson({ lessonType, isCaregiver })) {
    return MIXPANEL_PROGRAM_TYPE.CAREGIVER;
  }

  const fromProgramType = firstNonEmptyProgramLabel([programType], {
    allowAgeGroups: false,
  });
  if (fromProgramType) return fromProgramType;

  const fromAccessContext = firstNonEmptyProgramLabel(
    [programName, multiCollectionName],
    { allowAgeGroups: false },
  );
  if (fromAccessContext) return fromAccessContext;

  if (unitName === "Educator Wellbeing") {
    return MIXPANEL_PROGRAM_TYPE.EDUCATOR_WELL_BEING_SERIES;
  }
  if (unitName === "Educator Onboarding") {
    return "Educator Onboarding";
  }
  if (
    unitId === CreatingResilientClassroomsId ||
    unitId === CreatingResilientAusClassroomsId
  ) {
    return MIXPANEL_PROGRAM_TYPE.CREATING_RESILIENT_CLASSROOMS;
  }
  if (unitUniqueName === BookStudyUnitName) {
    return MIXPANEL_PROGRAM_TYPE.PROFESSIONAL_DEVELOPMENT;
  }

  for (const candidate of [courseName, unitName]) {
    const normalized = normalizeMixpanelProgramType(candidate);
    if (normalized && isKnownMixpanelProgramType(normalized)) {
      return normalized;
    }
  }

  const fromUnitOrCourse = firstNonEmptyProgramLabel([unitName, courseName], {
    allowAgeGroups: false,
  });
  if (fromUnitOrCourse) return fromUnitOrCourse;

  // Last resort: prefer any access-context label (including course year groups)
  // over Mixpanel "(not set)" / empty string.
  const lastResort = firstNonEmptyProgramLabel(
    [multiCollectionName, programName, courseName, unitName, programType],
    { allowAgeGroups: true },
  );
  return lastResort || MIXPANEL_PROGRAM_TYPE.STUDENT_VOICE_SERIES;
};

type ResolveMixpanelMultiCollectionNamesArgs = {
  multiCollections?: Array<{ name?: string | null } | null> | null;
  /** Multi-collection the lesson was opened from (Lesson Library / program nav) */
  mcName?: string | null;
};

/**
 * Resolves Mixpanel `multi_collection_name` from lesson membership and/or the
 * multi-collection access context.
 */
export const resolveMixpanelMultiCollectionNames = ({
  multiCollections,
  mcName,
}: ResolveMixpanelMultiCollectionNamesArgs): string[] => {
  const names = (multiCollections ?? [])
    .map((mc) => (mc?.name ?? "").trim())
    .filter(Boolean);

  const accessName = (mcName ?? "").trim();
  if (accessName && !names.includes(accessName)) {
    names.unshift(accessName);
  }

  return names;
};

/** Always include multi_collection_name — "None" when unknown (avoids "(not set)"). */
export const mixpanelMultiCollectionProps = (
  args: ResolveMixpanelMultiCollectionNamesArgs = {},
): { multi_collection_name: string[] } => {
  const names = resolveMixpanelMultiCollectionNames(args);
  return {
    multi_collection_name: names.length ? names : [MIXPANEL_NONE],
  };
};

/** How many grades a stored value names, expanding spans such as "Grade 3-4" or "Year 3-4". */
const countGradeValues = (value: string): number =>
  expandAustraliaAgeGroups(
    parseAgeGroupValues(value)
      .filter((entry): entry is string => typeof entry === "string")
      .flatMap((entry) => expandGradeLevel(entry)),
  ).length;

type ResolveMixpanelUnitGradeArgs = {
  /** Program/course name — for AU units this is the year level (e.g. "Year 5") */
  courseName?: string | null;
  /** Existing grade if already known */
  grade?: string | null;
};

/**
 * Resolves Mixpanel `grade` for unit/course lessons.
 * AU units store year levels on the course/program name; copy that into `grade`.
 * Falls back to "None" so Mixpanel never shows "(not set)".
 */
export const resolveMixpanelUnitGrade = ({
  courseName,
  grade,
}: ResolveMixpanelUnitGradeArgs): string => {
  const existing = (grade ?? "").trim();
  if (existing) return existing;

  const fromCourse = (courseName ?? "").trim().replace(/\s*Program\s*$/i, "");
  return fromCourse || MIXPANEL_NONE;
};

/**
 * Resolves Mixpanel `grade` for standalone lessons (ageGroup / gradeLevel).
 * Falls back to "None" so Mixpanel never shows "(not set)".
 */
export const resolveMixpanelStandaloneGrade = ({
  ageGroup,
  gradeLevel,
}: {
  ageGroup?: string | null;
  gradeLevel?: string | null;
}): string => {
  const au = (ageGroup ?? "").trim();
  const na = (gradeLevel ?? "").trim();

  // Most lessons carry one column or the other, but some carry both and the two can disagree, for
  // instance "Grade 6" beside ["Grade 6","Grade 5"]. Reading whichever is filled in first then
  // reports a narrower range than the lesson actually spans, and can name a grade the teacher did
  // not teach. Prefer whichever column names more grades, and return it as stored so the value
  // keeps the shape analytics already groups on.
  const value = countGradeValues(au) >= countGradeValues(na) ? au || na : na;
  return value || MIXPANEL_NONE;
};
